import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import BasicLayout from '../components/Shared/BasicLayout';
import PlanCheckout from '../components/Paywall/PlanCheckout';
import Constants from '../constants';
import { UserContext } from '../contexts/user';
import {
  normalizeSubscriptionPlan,
  organizationNeedsPlan,
  useSubscription,
} from '../contexts/subscription';
import { PAYWALL_FEATURES } from '../utils/paywall';

function hasSession(): boolean {
  try {
    return !!localStorage.getItem('token');
  } catch {
    return false;
  }
}

export default function Plans(): JSX.Element {
  const { state } = React.useContext(UserContext);
  const subscription = useSubscription();
  const planOnUser = normalizeSubscriptionPlan(state.user?.organization?.subscriptionPlan);
  const currentPlan = subscription.status === 'ready' ? subscription.planId : planOnUser;
  const needsPlan = organizationNeedsPlan({
    oppText: !!Constants.oppText,
    wpAdmin: state.user?.wpAdmin,
    status: subscription.status,
    authoritative: subscription.authoritative,
    planId: subscription.planId,
    trialActive: subscription.trialActive,
  });
  const content = (
    <View style={styles.page}>
      <PlanCheckout required={needsPlan} />
      {currentPlan ? null : (
        <>
          <Text style={styles.sectionTitle}>Included on every plan</Text>
          <View style={styles.featureList}>
            {PAYWALL_FEATURES.map((feature) => (
              <View key={feature.id} style={styles.featureRow}>
                <View style={styles.mark}>
                  <Text style={styles.markText}>✓</Text>
                </View>
                <View style={styles.featureCopy}>
                  <Text style={styles.featureName}>{feature.name}</Text>
                  <Text style={styles.featureDetail}>{feature.detail}</Text>
                </View>
                <Text style={styles.includedLabel}>Included</Text>
              </View>
            ))}
          </View>
        </>
      )}
    </View>
  );

  if (!hasSession()) {
    return <View style={styles.standalone}>{content}</View>;
  }
  return <BasicLayout active="/plans">{content}</BasicLayout>;
}

const styles = StyleSheet.create({
  standalone: {
    flex: 1,
    minHeight: '100vh' as unknown as number,
    backgroundColor: Constants.colors.backgroundLight,
  },
  page: {
    maxWidth: 1080,
  },
  sectionTitle: {
    fontFamily: 'GothamBold',
    fontSize: 18,
    color: Constants.colors.dark,
    marginTop: 8,
    marginHorizontal: 28,
  },
  featureList: {
    backgroundColor: Constants.colors.white,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Constants.colors.secondaryLight,
    overflow: 'hidden',
    marginHorizontal: 28,
    marginTop: 12,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: Constants.colors.light,
    gap: 12,
  },
  mark: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Constants.colors.lightBlue,
  },
  markText: {
    color: Constants.colors.darkBlue,
    fontFamily: 'GothamBold',
    fontSize: 13,
  },
  featureCopy: {
    flex: 1,
  },
  featureName: {
    fontFamily: 'GothamMedium',
    fontSize: 15,
    color: Constants.colors.dark,
  },
  featureDetail: {
    fontFamily: 'Gotham',
    fontSize: 13,
    color: Constants.colors.secondary,
    marginTop: 2,
  },
  includedLabel: {
    fontFamily: 'GothamMedium',
    fontSize: 13,
    color: Constants.colors.darkGreen,
  },
});
